import Title_ru_RU_Class     from "@/core/misc/decorators/Title_ru_RU_Class";
import Title_ru_RU           from "@/core/misc/decorators/Title_ru_RU";
import TYPES                 from '@/core/services/Changelogs/types';
import Core                  from '@/core/Core';
import DateRange             from '@/core/types/DateRange';
import DateSimple            from "@/core/types/DateSimple";
import Qty                   from "@/core/types/Qty";
import AbstractOrderPosition from "@/domains/Abstract/AbstractOrderPosition";

import type Offer            from "@/domains/Offers/Offer";
import type Demand           from "@/domains/Demand/Demand";
import TenderBid from "@/domains/TenderBids/TenderBid";
import Tender from "@/domains/Tenders/Tender";

/**
 * Позиция предзаказа.
 *
 * Предзаказ как документ существует один раз — в момент создания. Дальше вся
 * работа идёт с позицией, поэтому предзаказные поля (период выборки, резерв,
 * остатки) живут здесь, а не в реквизитах документа.
 */
@Title_ru_RU_Class('Позиция предзаказа')            // preorderPosition → PreorderPosition
class PreorderPosition extends AbstractOrderPosition {

  /**
   * Состояния позиции предзаказа. Совпадают с вкладками воронки и
   * фильтрами PreordersService.getStatusFilter.
   */
  static StatusNames = {
    'in-work':   'В работе',
    'delivered': 'Доставлен',
    'cancelled': 'Отменён',
  } as const;

  @Title_ru_RU('период выборки по предзаказу', TYPES.DATE_RANGE)
  public preorderShipmentPeriod: DateRange;

  @Title_ru_RU('минимальный месячный резерв на складе', TYPES.NUMBER)
  public preorderMonthlyReserve: Qty | null;

  /** Минимальная партия предзаказа */
  public preorderMinQty: Qty | null;

  /** Кол-во из предзаказа, по которому оформлены заказы */
  public qtyOrdered: Qty;

  /** Количество к предзаказу осталось */
  @Title_ru_RU('количество осталось', TYPES.NUMBER)
  public qtyLeft: Qty;

  /**
   * Предзаказ из тендера котировок (свободный выкуп).
   * Наследуется от тендера при создании предзаказа
   */
  public isQuote: boolean = false;

  static fromServer(data: any, result = new PreorderPosition()): PreorderPosition {
    super.fromServer(data, result);

    result.preorderShipmentPeriod = Core.utils.hydrate(data.preorderShipmentPeriod, DateRange.fromServer)!;
    result.preorderMonthlyReserve = Core.utils.hydrate(data.preorderMonthlyReserve, Qty.fromServer);
    result.preorderMinQty         = Core.utils.hydrate(data.preorderMinQty, Qty.fromServer);
    result.qtyOrdered             = Core.utils.hydrate(data.qtyOrdered, Qty.fromServer)!;
    result.qtyLeft                = Core.utils.hydrate(data.qtyLeft, Qty.fromServer)!;
    result.isQuote                = data.isQuote === true;

    return result;
  }

  static fromOffer(offer: Offer, demand: Demand | null = null, result = new PreorderPosition()): PreorderPosition {
    super.fromOffer(offer, demand, result);

    result.preorderMonthlyReserve = offer.preorderMonthlyReserve;
    result.preorderMinQty         = offer.preorderMinQty;

    return result;
  }

  static fromTenderBid(tenderBid: TenderBid, tender: Tender, result = new PreorderPosition()) {
    super.fromTenderBid(tenderBid, tender, result);

    result.preorderShipmentPeriod = tender.preorderShipmentPeriod;
    result.preorderMonthlyReserve = tender.preorderMonthlyReserve;
    result.isQuote                = tender.isQtyOrderedFlexible;
    return result;
  }

  /**
   * Клон позиции из истории закупок для добавления в предзаказ.
   * Берём почти всё (товар, коды, количество, цены, НДС, температуру),
   * но сбрасываем идентичность и всё, что не должно переезжать в новую позицию.
   * Реквизиты НЕ клонируем — их выдаст предзаказ в addPosition (позиция «под заказ»).
   */
  static fromPreorderPosition(source: PreorderPosition): PreorderPosition {
    const result = Core.utils.cloneDeep(source);

    // новая сущность для бэка — сбрасываем id/version, иначе копия уйдёт на
    // сохранение под чужим id и перезапишет исходную позицию
    result.resetIdentity();
    // связь со строкой реестра-источника держит отдельное поле, а не id:
    // по нему реестр показывает добавленные позиции отмеченными
    result.sourcePositionId = source.id;

    // Сброс полей, которые не должны переезжать в новую позицию.
    // Object.assign не проверяет nullability отдельных полей, а satisfies ловит опечатки в именах.
    Object.assign(result, {
      // ---- Ссылки на источник не переносим ----
      order: null,
      isQuote: false,
    } satisfies Partial<Record<keyof PreorderPosition, unknown>>);

    return result;
  }

  /**
   * Период получения: период отрузки плюс срок доставки. У предзаказа это
   * окно, а не одна дата, как у позиции заказа, поэтому сдвигаются обе границы.
   */
  get receiptPeriod(): DateRange | null {
    if (
      this.preorderShipmentPeriod?.isFilled() !== true
      || Core.utils.isNullOrUndefined(this.requisites?.deliveryDays) === true
    ) {
      return null;
    }
    else {
      return this.preorderShipmentPeriod.addDays(this.requisites.deliveryDays);
    }
  }

  get status(): keyof typeof PreorderPosition.StatusNames {
    if (this.isDeleted === true) {
      return 'cancelled';
    }

    if (this.requisites.isFinished === true) {
      return 'delivered';
    }

    return 'in-work';
  }

  get statusName(): string {
    return PreorderPosition.StatusNames[this.status];
  }

  /**
   * Валидация начала периода выборки.
   * Не даём указать раньше минимально возможной даты доставки.
   * Перекрёстную проверку start ≤ end делать не нужно — VDateRangeInput
   * сам нормализует диапазон при вводе.
   */
  validatePreorderShipmentPeriodFrom(value: DateSimple | null | Object): string | null {
    if (Core.utils.isNullOrUndefined(this.requisites.deliveryDays) || value === null) {
      return null;
    }

    // Явное сравнение вместо !(...): читается сразу, без мысленной инверсии
    if (value instanceof DateSimple === false) {
      value = new DateSimple(value);
    }

    const minDate = DateSimple.today().addDays(this.requisites.deliveryDays);
    if (value.isBefore(minDate)) {
      return `Нельзя указать раньше, чем смогут доставить. Минимальная возможная дата ${minDate.toLocalString()}`;
    }

    return null;
  }

  /**
   * Причина, по которой позицию нельзя редактировать. null — можно.
   * Админу разрешено всегда.
   */
  getReasonCannotEdit(): string | null {
    if (Core.currentUser.isAdmin === true) {
      return null;
    }

    if (this.isDeleted === true) {
      return 'Позиция удалена';
    }

    if (this.isFinished === true) {
      return 'Позиция завершена';
    }

    return null;
  }

  /**
   * Причины, по которым нельзя менять реквизиты сделки на позиции.
   * Позиция предзаказа - редактируем всегда, карточки заказа у предзаказа нет.
   */
  getReasonsCannotChangeDealTerms(): string[] {
    return [];
  }
}

export default PreorderPosition;
